import { API_BASE_URL } from "../config/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import PhoneActions from "../components/PhoneActions";

const STATUS_CONFIG = {
  pending: { label: "Pending", bg: "bg-amber-100 text-amber-800 border-amber-200" },
  assigned: { label: "Assigned", bg: "bg-blue-100 text-blue-800 border-blue-200" },
  "in progress": { label: "In Progress", bg: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  "re-visit": { label: "Re-Visit", bg: "bg-purple-100 text-purple-800 border-purple-200" },
  completed: { label: "Completed", bg: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  closed: { label: "Closed", bg: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

const getStatusBadge = (status = "") => {
  const norm = String(status || "").trim().toLowerCase();
  const config = STATUS_CONFIG[norm] || { label: status || "Pending", bg: "bg-slate-100 text-slate-700 border-slate-200" };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${config.bg}`}>
      {config.label}
    </span>
  );
};

const formatDate = (val) => {
  if (!val) return "-";
  const str = String(val).trim();
  const match = str.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  if (match) return `${match[1]}/${match[2]}/${match[3]}`;
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return str;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export default function AbbWarrantyComplaints() {
  const navigate = useNavigate();
  const role = localStorage.getItem("role") || "";
  const isAccounts = role === "accounts";
  const [complaints, setComplaints] = useState([]);
  const [reportMap, setReportMap] = useState({});
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedComplaint, setSelectedComplaint] = useState(null);

  // Quick Create Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createForm, setCreateForm] = useState({
    company: "",
    address: "",
    contactName: "",
    contactPhone: "",
    engineerAssigned: "",
    abbTicketNo: "",
    issue: "",
    visitDate: new Date().toISOString().slice(0, 10),
  });
  const [formError, setFormError] = useState("");

  const fetchEngineers = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(API_BASE_URL + "/api/engineers", {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setEngineers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load engineers", err);
    }
  }, []);

  const fetchComplaintsAndVisits = useCallback(async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [complaintsRes, visitsRes] = await Promise.allSettled([
        axios.get(API_BASE_URL + "/api/complaints?type=abb_warranty", { headers }),
        axios.get(API_BASE_URL + "/api/visits?excludePdf=1", { headers })
      ]);

      const complaintData = complaintsRes.status === "fulfilled" && Array.isArray(complaintsRes.value.data)
        ? complaintsRes.value.data
        : [];

      const visitData = visitsRes.status === "fulfilled" && Array.isArray(visitsRes.value.data)
        ? visitsRes.value.data
        : [];

      // Map reports by complaint IDs
      const rMap = {};
      visitData.forEach((v) => {
        [v.complaintId, v.complaintRefId, v._id, v.visitId].filter(Boolean).forEach((k) => {
          const normK = String(k).trim();
          if (!rMap[normK] || new Date(v.createdAt) > new Date(rMap[normK].createdAt)) {
            rMap[normK] = v;
          }
        });
      });
      setReportMap(rMap);

      const abbOnly = complaintData.filter((c) =>
        c.isAbbWarranty ||
        c.productType === "ABB Warranty" ||
        c.customerId === "ABB-WARRANTY" ||
        String(c.company || "").toLowerCase().includes("abb") ||
        String(c.siteAdvice || "").toLowerCase().includes("abb warranty")
      );
      setComplaints(abbOnly);
    } catch (err) {
      console.error("Failed to fetch ABB warranty complaints", err);
      setComplaints([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchEngineers();
    fetchComplaintsAndVisits();
  }, [fetchEngineers, fetchComplaintsAndVisits]);

  // Filtered complaints
  const filteredComplaints = useMemo(() => {
    return complaints.filter((c) => {
      if (!search.trim()) return true;
      return [c.complaintId, c.company, c.address, c.contactName, c.contactPhone, c.engineerAssigned, c.abbTicketNo, c.issue]
        .filter(Boolean)
        .some((val) => String(val).toLowerCase().includes(search.toLowerCase().trim()));
    });
  }, [complaints, search]);

  const handleOpenReport = (item, e) => {
    if (e) e.stopPropagation();
    const linkedReport = reportMap[item._id] || reportMap[item.complaintId] || (item.reportId ? { _id: item.reportId } : null);

    if (linkedReport && linkedReport._id) {
      navigate(isAccounts ? `/accounts/reports/${linkedReport._id}` : `/reports/${linkedReport._id}`);
    } else {
      const confirmCreate = window.confirm(
        `No inspection report has been generated yet for ABB Ticket #${item.complaintId}.\n\nWould you like to create the service report now?`
      );
      if (confirmCreate) {
        navigate(`/reports/merge/create?complaintId=${item._id}`);
      }
    }
  };

  const handleOpenCreateModal = () => {
    setCreateForm({
      company: "",
      address: "",
      contactName: "",
      contactPhone: "",
      engineerAssigned: "",
      abbTicketNo: "",
      issue: "",
      visitDate: new Date().toISOString().slice(0, 10),
    });
    setFormError("");
    setShowCreateModal(true);
  };

  const handleQuickCreateSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!createForm.company.trim()) {
      setFormError("Customer / Company name is required.");
      return;
    }
    if (!createForm.address.trim()) {
      setFormError("Customer address / site location is required.");
      return;
    }
    if (!createForm.issue.trim()) {
      setFormError("Complaint issue / nature of problem is required.");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const matchedEng = engineers.find(
        (eng) => eng.name === createForm.engineerAssigned || eng.engineerId === createForm.engineerAssigned
      );

      const payload = {
        company: createForm.company.trim(),
        customerId: "ABB-WARRANTY",
        isAbbWarranty: true,
        productType: "ABB Warranty",
        type: "abb_warranty",
        address: createForm.address.trim(),
        contactName: createForm.contactName.trim(),
        contactPhone: createForm.contactPhone.trim(),
        abbTicketNo: createForm.abbTicketNo.trim(),
        issue: createForm.issue.trim(),
        visitDate: createForm.visitDate,
        engineerAssigned: matchedEng ? matchedEng.name : createForm.engineerAssigned,
        engineerAssignedId: matchedEng ? matchedEng.engineerId : "",
        engineerAssignedEmail: matchedEng ? matchedEng.email : "",
        assignedEngineers: matchedEng
          ? [{ engineerId: matchedEng.engineerId, engineerName: matchedEng.name, engineerEmail: matchedEng.email, team: "" }]
          : [],
      };

      const res = await axios.post(API_BASE_URL + "/api/complaints", payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setShowCreateModal(false);
      fetchComplaintsAndVisits();
      alert(`ABB Warranty Complaint "${res.data.complaintId || "Created"}" saved successfully!`);
    } catch (err) {
      console.error("Failed to create ABB warranty complaint", err);
      setFormError(err.response?.data?.message || "Failed to create ABB warranty complaint. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComplaint = async (c) => {
    if (!window.confirm(`Are you sure you want to delete ABB Complaint #${c.complaintId}?`)) {
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(API_BASE_URL + `/api/complaints/${c._id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setComplaints((prev) => prev.filter((item) => item._id !== c._id));
      if (selectedComplaint?._id === c._id) setSelectedComplaint(null);
    } catch (err) {
      console.error("Failed to delete complaint", err);
      alert(err.response?.data?.message || "Failed to delete complaint");
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-label-caps text-[11px] text-teal-600 uppercase font-bold tracking-wider">
              CONTACTS &bull; ABB SERVICE DIVISION
            </span>
          </div>
          <h1 className="text-2xl font-black tracking-tight text-slate-950 sm:text-3xl flex items-center gap-2">
            <span className="material-symbols-outlined text-teal-600 text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>
              verified
            </span>
            ABB Warranty Complaints
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Click on any Complaint ID / Ticket to directly open and view its service report.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={fetchComplaintsAndVisits}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition shadow-sm"
          >
            <span className={`material-symbols-outlined text-base ${refreshing ? "animate-spin" : ""}`}>
              sync
            </span>
            Refresh
          </button>

          <button
            type="button"
            onClick={handleOpenCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition shadow-sm"
          >
            <span className="material-symbols-outlined text-base">add_circle</span>
            + New ABB Complaint
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
        <div className="relative">
          <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-lg">
            search
          </span>
          <input
            type="text"
            placeholder="Search by Complaint ID, Customer Name, Address, Contact, Attended Engineer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-10 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 text-base font-bold"
            >
              &times;
            </button>
          )}
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-20 text-center text-slate-400">
            <span className="material-symbols-outlined text-4xl animate-spin mb-2 text-teal-600">
              progress_activity
            </span>
            <p className="text-sm font-semibold">Loading ABB Warranty Complaints...</p>
          </div>
        ) : filteredComplaints.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            <span className="material-symbols-outlined text-5xl text-slate-300 mb-2">
              verified
            </span>
            <h3 className="text-base font-bold text-slate-800">No ABB Warranty Complaints Found</h3>
            <p className="text-sm text-slate-400 max-w-md mx-auto mt-1 mb-4">
              {search
                ? "No complaints match your search query."
                : "No ABB warranty complaints registered yet. Click below to add the first ABB complaint."}
            </p>
            <button
              type="button"
              onClick={handleOpenCreateModal}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition"
            >
              <span className="material-symbols-outlined text-base">add_circle</span>
              Create ABB Warranty Complaint
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-600">
                  <th className="py-3.5 px-4">Complaint ID (Click for Report)</th>
                  <th className="py-3.5 px-4">ABB Customer</th>
                  <th className="py-3.5 px-4">Address / Site</th>
                  <th className="py-3.5 px-4">Contact</th>
                  <th className="py-3.5 px-4">Attended By</th>
                  <th className="py-3.5 px-4">Issue Details</th>
                  <th className="py-3.5 px-4">Visit Date</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredComplaints.map((item) => {
                  const hasReport = Boolean(reportMap[item._id] || reportMap[item.complaintId] || item.reportId);

                  return (
                    <tr
                      key={item._id}
                      className="hover:bg-teal-50/40 transition-colors group cursor-pointer"
                      onClick={(e) => handleOpenReport(item, e)}
                    >
                      {/* Complaint ID / Ticket (Clicking opens report) */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={(e) => handleOpenReport(item, e)}
                          title={hasReport ? "Click to open Service Report" : "Click to create / view Service Report"}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-teal-50 hover:bg-teal-600 text-teal-800 hover:text-white border border-teal-200 hover:border-teal-600 font-mono font-bold text-xs transition-all shadow-2xs group/btn"
                        >
                          <span className="material-symbols-outlined text-sm text-teal-600 group-hover/btn:text-white transition-colors">
                            {hasReport ? "description" : "note_add"}
                          </span>
                          <span>{item.complaintId}</span>
                          <span className="text-[10px] uppercase font-extrabold px-1 py-0.2 bg-teal-200 text-teal-900 group-hover/btn:bg-white group-hover/btn:text-teal-800 rounded">
                            {hasReport ? "REPORT" : "OPEN"}
                          </span>
                        </button>
                      </td>

                      {/* Customer Name */}
                      <td className="py-3.5 px-4">
                        <div className="font-bold text-slate-900 group-hover:text-teal-700 transition">
                          {item.company || "Unnamed Customer"}
                        </div>
                      </td>

                      {/* Address / Location */}
                      <td className="py-3.5 px-4 max-w-xs">
                        <div className="flex items-start gap-1 text-slate-700 text-xs line-clamp-2">
                          <span className="material-symbols-outlined text-slate-400 text-sm mt-0.5 shrink-0">
                            location_on
                          </span>
                          <span>{item.address || "-"}</span>
                        </div>
                      </td>

                      {/* Contact Person & Phone */}
                      <td className="py-3.5 px-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="text-slate-800 text-xs font-medium">
                          {item.contactName || "-"}
                        </div>
                        {item.contactPhone ? (
                          <div className="mt-0.5">
                            <PhoneActions phone={item.contactPhone} size="sm" />
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </td>

                      {/* Attended By / Assigned Engineer */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        {item.engineerAssigned ? (
                          <div className="flex items-center gap-1.5">
                            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold">
                              {item.engineerAssigned.charAt(0).toUpperCase()}
                            </span>
                            <span className="font-semibold text-slate-800 text-xs">
                              {item.engineerAssigned}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs italic">Unassigned</span>
                        )}
                      </td>

                      {/* Issue Details */}
                      <td className="py-3.5 px-4 max-w-xs">
                        <div className="text-xs text-slate-600 truncate max-w-[220px]" title={item.issue}>
                          {item.issue || "-"}
                        </div>
                      </td>

                      {/* Visit Date */}
                      <td className="py-3.5 px-4 whitespace-nowrap text-xs text-slate-600">
                        {formatDate(item.visitDate || item.createdAt)}
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        {getStatusBadge(item.status)}
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={(e) => handleOpenReport(item, e)}
                            className="p-1.5 rounded-lg text-teal-700 bg-teal-50 hover:bg-teal-100 transition"
                            title="Open Service Report"
                          >
                            <span className="material-symbols-outlined text-lg">description</span>
                          </button>
                          <Link
                            to={`/complaints/${item._id}/edit`}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-blue-700 hover:bg-blue-50 transition"
                            title="Edit Complaint"
                          >
                            <span className="material-symbols-outlined text-lg">edit</span>
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleDeleteComplaint(item)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition"
                            title="Delete Complaint"
                          >
                            <span className="material-symbols-outlined text-lg">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* QUICK CREATE MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-teal-100 flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-teal-600 text-white rounded-t-2xl">
              <div className="flex items-center gap-2.5">
                <span className="p-2 bg-white/20 rounded-lg">
                  <span className="material-symbols-outlined text-xl">verified</span>
                </span>
                <div>
                  <h3 className="text-lg font-bold">New ABB Warranty Complaint</h3>
                  <p className="text-xs text-teal-100">
                    Direct entry &bull; No customer creation required
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10"
              >
                <span className="material-symbols-outlined text-2xl">close</span>
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleQuickCreateSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-2">
                  <span className="material-symbols-outlined text-base">error</span>
                  <span>{formError}</span>
                </div>
              )}

              {/* Customer Name */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  ABB Customer / Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Jindal Steel / ABC Industries"
                  value={createForm.company}
                  onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })}
                  required
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-lg text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 font-medium"
                />
              </div>

              {/* Address / Location */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Customer Address / Site Location <span className="text-red-500">*</span>
                </label>
                <textarea
                  placeholder="e.g. Plot No 42, Industrial Area, Peenya, Bangalore"
                  value={createForm.address}
                  onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })}
                  required
                  rows={2}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Contact Person & Contact Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Contact Person Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Ramesh Kumar"
                    value={createForm.contactName}
                    onChange={(e) => setCreateForm({ ...createForm, contactName: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Contact Phone Number
                  </label>
                  <input
                    type="tel"
                    placeholder="e.g. 9876543210"
                    value={createForm.contactPhone}
                    onChange={(e) => setCreateForm({ ...createForm, contactPhone: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              {/* Attended By & Visit Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Attended By / Engineer
                  </label>
                  <select
                    value={createForm.engineerAssigned}
                    onChange={(e) => setCreateForm({ ...createForm, engineerAssigned: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">-- Select Attended Engineer --</option>
                    {engineers.map((eng) => (
                      <option key={eng._id || eng.engineerId} value={eng.name}>
                        {eng.name} ({eng.engineerId || "ENG"})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Visit / Call Date
                  </label>
                  <input
                    type="date"
                    value={createForm.visitDate}
                    onChange={(e) => setCreateForm({ ...createForm, visitDate: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              {/* ABB Ticket No */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-slate-700 uppercase">
                    ABB Warranty Ticket Number
                  </label>
                  {createForm.abbTicketNo && (
                    <span className="text-[11px] font-mono font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded border border-teal-200">
                      Ticket ID: C{String(new Date().getMonth() + 1)}{String(new Date().getFullYear()).slice(-2)}/ABB-{createForm.abbTicketNo.replace(/^C\d+\/ABB-?/i, "").replace(/^ABB-?/i, "").trim() || createForm.abbTicketNo.trim()}
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="e.g. 123 / ABB-123 / C926/ABB-123"
                  value={createForm.abbTicketNo}
                  onChange={(e) => setCreateForm({ ...createForm, abbTicketNo: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-lg text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 font-medium"
                />
              </div>

              {/* Issue Description */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Complaint Issue / Details <span className="text-red-500">*</span>
                </label>
                <textarea
                  placeholder="Describe the breaker fault, trip alarm, or warranty service required..."
                  value={createForm.issue}
                  onChange={(e) => setCreateForm({ ...createForm, issue: e.target.value })}
                  required
                  rows={3}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Footer */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition shadow-sm flex items-center gap-2"
                >
                  {submitting && (
                    <span className="material-symbols-outlined text-base animate-spin">
                      progress_activity
                    </span>
                  )}
                  {submitting ? "Saving..." : "Create ABB Complaint"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
